<script setup lang="ts">
import { computed } from 'vue'
import { Pencil } from 'lucide-vue-next'
import SectionHead from './SectionHead.vue'
import CopyableText from '../../CopyableText.vue'
import type { Employee, Promotion } from '../../../types/supplier'

const props = defineProps<{
  websiteUrl?: string
  categories: string[]
  employees: Employee[]
  promotions: Promotion[]
  counts: { products: number; training: number; documents: number; contacts: number }
  canEdit: boolean
}>()
defineEmits<{ seeAllContacts: [] }>()

const none = (n: number) => n ? String(n) : 'None'
const details = computed(() => [
  { label: 'Categories', value: props.categories.join(', ') || 'None' },
  { label: 'Products', value: none(props.counts.products) },
  { label: 'Channel contacts', value: none(props.counts.contacts) },
  { label: 'Training videos', value: none(props.counts.training) },
  { label: 'Active promotions', value: none(props.promotions.length) },
  { label: 'Documents', value: none(props.counts.documents) },
])
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
const fmtDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
</script>

<template>
  <aside class="space-y-4" aria-label="Company summary">
    <section class="csp-panel px-6 py-5" aria-labelledby="csp-details">
      <SectionHead id="csp-details" title="Details">
        <router-link v-if="canEdit" :to="{ name: 'supplier-settings' }" class="csp-link inline-flex items-center gap-1">
          <Pencil :size="13" aria-hidden="true" /> Edit
        </router-link>
      </SectionHead>
      <dl class="text-[13px]">
        <div class="grid grid-cols-[120px_1fr] gap-3 py-2 border-b border-(--csp-divider)">
          <dt class="csp-muted">Website</dt>
          <dd class="min-w-0 truncate">
            <a v-if="websiteUrl" :href="websiteUrl" target="_blank" rel="noopener" class="csp-link">{{ websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') }}</a>
            <span v-else class="text-(--csp-text)">None</span>
          </dd>
        </div>
        <div v-for="d in details" :key="d.label" class="grid grid-cols-[120px_1fr] gap-3 py-2 border-b border-(--csp-divider) last:border-b-0">
          <dt class="csp-muted">{{ d.label }}</dt>
          <dd class="text-(--csp-text)">{{ d.value }}</dd>
        </div>
      </dl>
    </section>

    <section class="csp-panel px-6 py-5" aria-labelledby="csp-contacts">
      <SectionHead id="csp-contacts" title="Channel contacts" :count="employees.length">
        <router-link v-if="canEdit" :to="{ name: 'supplier-contacts' }" class="csp-link inline-flex items-center gap-1"><Pencil :size="13" aria-hidden="true" /> Manage</router-link>
      </SectionHead>
      <ul>
        <li v-for="e in employees.slice(0, 5)" :key="e.id" class="flex items-center gap-3 py-2">
          <span class="w-8 h-8 rounded-full bg-(--csp-tag) text-(--csp-text-2) text-xs font-semibold flex items-center justify-center shrink-0" aria-hidden="true">{{ initials(e.name) }}</span>
          <div class="min-w-0 flex-1">
            <p class="text-[13px] font-semibold text-(--csp-text) truncate">{{ e.name }}</p>
            <a :href="`mailto:${e.email}`" class="block text-xs csp-muted hover:underline truncate">{{ e.email }}</a>
          </div>
          <CopyableText :value="e.email" kind="email" icon-only class="csp-muted" />
        </li>
        <li v-if="!employees.length" class="py-2 text-[13px] csp-muted">No channel contacts yet.</li>
      </ul>
      <button v-if="employees.length > 5" type="button" class="csp-link text-[13px] mt-2" @click="$emit('seeAllContacts')">
        See all {{ employees.length }} contacts
      </button>
    </section>

    <section class="csp-panel px-6 py-5" aria-labelledby="csp-promotions">
      <SectionHead id="csp-promotions" title="Promotions" :count="promotions.length || undefined" />
      <template v-if="promotions.length">
        <ul>
          <li v-for="p in promotions.slice(0, 3)" :key="p.id" class="py-2 border-b border-(--csp-divider) last:border-b-0">
            <p class="text-[13px] font-semibold text-(--csp-text) truncate">{{ p.name }}</p>
            <p class="text-xs csp-muted">{{ p.validUntil ? `Ends ${fmtDate(p.validUntil)}` : 'No end date' }}</p>
          </li>
        </ul>
      </template>
      <p v-else class="text-[13px] csp-muted">No active promotions. Live promotions appear on advisors’ dashboards and in search results.</p>
      <router-link v-if="canEdit" :to="{ name: 'supplier-promotions' }" class="csp-link text-[13px] inline-block mt-2">Create a promotion</router-link>
    </section>
  </aside>
</template>
